// src/App.js
// Updated: 11/10/2025 09:55 PM EST
// --- THIS IS THE FULL, REPLACEMENT FILE ---

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { db } from './firebaseConfig';
import { doc, getDoc } from 'firebase/firestore'; 

// Import Layouts
import AppLayout from './components/AppLayout';

// Import Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentViewerPage from './pages/DocumentViewerPage';
import VotingPage from './pages/VotingPage';
import ScoringPage from './pages/ScoringPage';
import ScorePackagesPage from './pages/ScorePackagesPage';
import ScoringResultsPage from './pages/ScoringResultsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfConditionsPage from './pages/TermsOfConditionsPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProfilePage from './pages/ProfilePage'; // <-- IMPORT PROFILE PAGE

// Import Admin Pages
import CreateScoringSessionPage from './pages/admin/CreateScoringSessionPage';
import ManageScoringSessionPage from './pages/admin/ManageScoringSessionPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';
import AdminSettingsPage from './pages/admin/SettingsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import SuperAdminUsersPage from './pages/admin/SuperAdminUsersPage';
import DetailedScoringReportPage from './pages/DetailedScoringReportPage';


const LoadingSpinner = () => ( 
  <div className="flex h-screen items-center justify-center bg-gray-100">
    <div className="h-16 w-16 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

// SessionManagerRoute - For routes requiring session management privileges
const SessionManagerRoute = ({ children }) => {
    const { currentUser, userData, isLoading } = useAuth();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const location = useLocation();

    // 1. Try to get Session ID from URL path (e.g., /manage/:sessionId)
    const sessionIdMatch = location.pathname.match(/\/admin\/scoring\/([a-zA-Z0-9]+)\/manage/);
    let sessionId = sessionIdMatch ? sessionIdMatch[1] : null;

    // 2. If not found, check URL query parameters (e.g., /new?edit=sessionId)
    if (!sessionId) {
        const queryParams = new URLSearchParams(location.search);
        sessionId = queryParams.get('edit');
    }
    
    // Global admin/super admin check
    const isGlobalAdmin = userData?.role === 'admin' || userData?.super_admin === true;

    useEffect(() => {
        if (isLoading || !currentUser || !userData) return;
        
        // 1. If global admin, grant access immediately
        if (isGlobalAdmin) {
            setIsAuthorized(true);
            setIsChecking(false);
            return;
        }

        // 2. If not admin, check for Session Sponsor status
        if (sessionId) {
            const checkSponsorStatus = async () => {
                try {
                    const sessionRef = doc(db, 'scoringSessions', sessionId);
                    const snap = await getDoc(sessionRef);
                    if (snap.exists()) {
                        const sponsors = snap.data().sessionSponsors || [];
                        if (sponsors.includes(currentUser.uid)) {
                            setIsAuthorized(true);
                        } else {
                            setIsAuthorized(false);
                            console.warn("Access Denied: Not a global admin or session sponsor.");
                        }
                    } else {
                        // For an *edit* that fails to find the session ID, it denies access.
                        setIsAuthorized(false);
                        console.warn("Access Denied: Session not found.");
                    }
                } catch (e) {
                    console.error("Error checking session sponsor status:", e);
                    setIsAuthorized(false);
                } finally {
                    setIsChecking(false);
                }
            };
            checkSponsorStatus();
        } else {
             // If there's no session ID (i.e., a user is trying to CREATE a *new* session)
             // Only admins can create new sessions, so non-admins are unauthorized.
             setIsAuthorized(false);
             setIsChecking(false);
        }
    }, [isLoading, currentUser, userData, isGlobalAdmin, sessionId]);

    if (isLoading || isChecking) return <LoadingSpinner />;
    if (!currentUser || !currentUser.emailVerified) return <Navigate to="/login" replace />;
    
    // Redirect if they aren't authorized (and we are done checking)
    if (!isAuthorized) {
        return <Navigate to="/dashboard" replace />;
    }

    return <AppLayout>{children}</AppLayout>;
}


// ProtectedRoute - Original component for general member/admin routes
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { currentUser, userData, isLoading } = useAuth();

  if (isLoading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/login" replace />;

  if (!currentUser.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  if (adminOnly && userData === null) return <LoadingSpinner />;
  
  // If the route requires admin, but the user is NOT an admin, redirect.
  if (adminOnly && userData?.role !== 'admin') {
      // Allow super admins to access regular admin pages
      if (userData?.super_admin !== true) {
        console.warn("Access Denied: User is not an admin.");
        return <Navigate to="/dashboard" replace />;
      }
  }

  return <AppLayout>{children}</AppLayout>;
};

// PublicRoute
const PublicRoute = ({ children }) => {
  const { currentUser, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (currentUser) return <Navigate to="/dashboard" replace />;
  return children;
};

// VerifyEmailRoute
const VerifyEmailRoute = ({ children }) => {
  const { currentUser, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/login" replace />; 
  if (currentUser.emailVerified) return <Navigate to="/dashboard" replace />; 
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* --- Public Routes --- */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/privacy" element={<PublicRoute><PrivacyPolicyPage /></PublicRoute>} />
        <Route path="/terms" element={<PublicRoute><TermsOfConditionsPage /></PublicRoute>} />
        <Route path="/verify-email" element={<VerifyEmailRoute><VerifyEmailPage /></VerifyEmailRoute>} />

        {/* --- Protected Routes (Members & Admins) --- */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} /> {/* <-- NEW ROUTE */}
        <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
        <Route path="/documents/:docId" element={<ProtectedRoute><DocumentViewerPage /></ProtectedRoute>} />
        <Route path="/voting" element={<ProtectedRoute><VotingPage /></ProtectedRoute>} />
        <Route path="/scoring" element={<ProtectedRoute><ScoringPage /></ProtectedRoute>} />
        <Route path="/scoring/:sessionId" element={<ProtectedRoute><ScorePackagesPage /></ProtectedRoute>} />
        <Route path="/scoring/:sessionId/results" element={<ProtectedRoute><ScoringResultsPage /></ProtectedRoute>} />
        
        {/* --- Reporting Route (Admin Only) --- */}
        <Route 
          path="/scoring/:sessionId/report" 
          element={<ProtectedRoute adminOnly={true}><DetailedScoringReportPage /></ProtectedRoute>} 
        />

        {/* --- Protected Admin Routes --- */}
        <Route path="/admin/scoring/new" element={<SessionManagerRoute><CreateScoringSessionPage /></SessionManagerRoute>} /> 
        <Route path="/admin/scoring/:sessionId/manage" element={<SessionManagerRoute><ManageScoringSessionPage /></SessionManagerRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute adminOnly={true}><ManageUsersPage /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute adminOnly={true}><AdminSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/audit-log" element={<ProtectedRoute adminOnly={true}><AuditLogPage /></ProtectedRoute>} />
        
        {/* --- Super Admin Route --- */}
        <Route path="/admin/super/users" element={<ProtectedRoute adminOnly={true}><SuperAdminUsersPage /></ProtectedRoute>} />

        {/* --- Default & Fallback Routes --- */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<ProtectedRoute><div>404 - Page Not Found</div></ProtectedRoute>} />

      </Routes>
    </BrowserRouter>
  );
}

export default App;